/**
 * Job-card "where" line — the only location shown on the card (TP-604).
 *
 * Twine list payload: `remote` + `location` (location_text). Fully remote is
 * remote=1 with no location; remote-within-country is remote=0 with a
 * country-only location; hybrid and on-site both write remote=0 with
 * "City, Country" (not distinguishable — defaults to On-site).
 *
 * Ari external cards: free-text `location` plus often "Contract · Remote" in
 * `type`. Pass `settingUnknown: true` so a place alone never becomes On-site.
 */

/**
 * Strip a trailing " · Remote" from an employment-type chip so Remote can live
 * on the where line instead.
 *
 * @param {string} workType
 * @returns {{ workType: string, remoteFromType: boolean }}
 */
export function splitWorkTypeRemote(workType = '') {
  const raw = String(workType || '').trim();
  if (!raw) {
    return { workType: '', remoteFromType: false };
  }

  const trailing = raw.match(/^(.*?)\s*[·•|]\s*Remote\s*$/i);
  if (trailing) {
    return {
      workType: trailing[1].trim(),
      remoteFromType: true,
    };
  }

  if (/^Remote$/i.test(raw)) {
    return { workType: '', remoteFromType: true };
  }

  return { workType: raw, remoteFromType: false };
}

/**
 * @param {string} location
 * @returns {{ place: string, isCountryOnly: boolean, country: string }}
 */
function parsePlace(location) {
  const raw = String(location || '').trim();
  if (!raw || /^remote$/i.test(raw)) {
    return { place: '', isCountryOnly: false, country: '' };
  }

  const parts = raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length > 1) {
    return {
      place: `${parts[0]}, ${parts.slice(1).join(', ')}`,
      isCountryOnly: false,
      country: parts[parts.length - 1],
    };
  }

  return { place: raw, isCountryOnly: true, country: raw };
}

/**
 * @param {'globe'|'pin'} icon
 * @param {string} primary
 * @param {string} [secondary]
 * @returns {{ icon: 'globe'|'pin', primary: string, secondary: string }}
 */
function where(icon, primary, secondary = '') {
  return { icon, primary, secondary };
}

/**
 * Build the where-line model for a Twine brief or normalised Ari location.
 *
 * Accepts a collaboration brief (`remote`, `location`) or an explicit bag:
 * `{ remote, location, hoursRestriction?, workSetting?, settingUnknown? }`.
 *
 * @param {Object} job
 * @returns {{ icon: 'globe'|'pin', primary: string, secondary: string }}
 */
export function formatWhereLine(job = {}) {
  if (!job || typeof job !== 'object') {
    return where('pin', 'Location not stated');
  }

  const isRemote = Boolean(job.remote);
  const { place, isCountryOnly } = parsePlace(job.location);
  const hours = String(job.hoursRestriction || job.timezoneHours || '').trim();
  const workSetting = job.workSetting || job.setting || null;
  // When the caller does not know remote/on-site/hybrid (Ari place-only),
  // never invent "On-site".
  const settingUnknown = Boolean(job.settingUnknown);

  if (isRemote) {
    if (hours) {
      // e.g. Remote · UK hours
      return where('globe', 'Remote', `${hours} hours`);
    }
    if (place && isCountryOnly) {
      return where('globe', 'Remote', `experts in ${place} only`);
    }
    if (place) {
      // "City, Country" is usually the employer's base, not an eligibility
      // restriction — show it as given.
      return where('globe', 'Remote', place);
    }
    return where('globe', 'Remote', 'work from anywhere');
  }

  // Twine remote-within-country: remote=0 + country-only location_text.
  if (place && isCountryOnly && !settingUnknown) {
    return where('globe', 'Remote', `experts in ${place} only`);
  }

  if (place) {
    if (settingUnknown) {
      return where('pin', place, 'setting not stated');
    }
    if (workSetting === 'hybrid') {
      return where('pin', 'Hybrid', place);
    }
    return where('pin', 'On-site', place);
  }

  return where('pin', 'Location not stated');
}
